// IndexedDB-backed offline support, two stores:
//  - outbox:      mutations made while the network was unavailable, replayed
//                 (in order) once it returns. A feed logged at 2am is never lost.
//  - eventsCache: the last-known event list per baby, so the app opens to the
//                 current day even with no connection.

import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { EventRow } from "../events";

export type OutboxOp =
  | { kind: "insert"; babyId: string; localId: string; row: EventRow }
  | { kind: "update"; babyId: string; targetId: string; patch: Partial<EventRow> }
  | { kind: "remove"; babyId: string; targetId: string };

// `seq` is the IndexedDB auto-increment key — absent until stored, present on read.
export type OutboxRecord = OutboxOp & { createdAt: string; seq?: number };
export type StoredOutbox = OutboxOp & { createdAt: string; seq: number };

interface BabyHelperDB extends DBSchema {
  outbox: { key: number; value: OutboxRecord };
  eventsCache: { key: string; value: { babyId: string; events: EventRow[]; cachedAt: string } };
}

let dbPromise: Promise<IDBPDatabase<BabyHelperDB>> | null = null;

function db(): Promise<IDBPDatabase<BabyHelperDB>> {
  if (!dbPromise) {
    dbPromise = openDB<BabyHelperDB>("babyhelper", 1, {
      upgrade(d) {
        d.createObjectStore("outbox", { keyPath: "seq", autoIncrement: true });
        d.createObjectStore("eventsCache", { keyPath: "babyId" });
      },
    });
  }
  return dbPromise;
}

/** Append a mutation to the outbox. Order is preserved by the auto-increment seq. */
export async function enqueue(op: OutboxOp): Promise<void> {
  const d = await db();
  await d.add("outbox", { ...op, createdAt: new Date().toISOString() });
}

/** All queued mutations for one baby, oldest first. */
export async function getOutbox(babyId: string): Promise<StoredOutbox[]> {
  const d = await db();
  const all = (await d.getAll("outbox")) as StoredOutbox[];
  return all.filter((e) => e.babyId === babyId).sort((a, b) => a.seq - b.seq);
}

export async function deleteOutbox(seq: number): Promise<void> {
  const d = await db();
  await d.delete("outbox", seq);
}

export async function cacheEvents(babyId: string, events: EventRow[]): Promise<void> {
  const d = await db();
  await d.put("eventsCache", { babyId, events, cachedAt: new Date().toISOString() });
}

export async function readCachedEvents(babyId: string): Promise<EventRow[] | null> {
  const d = await db();
  const rec = await d.get("eventsCache", babyId);
  return rec?.events ?? null;
}
